import { useState, useEffect, useCallback } from 'react';
import { Wrench, Save, Check, Loader2, MessageSquare, Hash, Zap, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { APP_CONFIG } from '../config/appConfig';
import { authFetch } from '../config/authConfig';

interface ConfigItem {
  id: number;
  clave: string;
  valor: string;
  tipo: string;
  descripcion: string;
  updated_at: string | null;
  updated_by: string;
}

const CONFIG_UI: Record<string, { icon: React.ComponentType<{ className?: string }>; label: string; suffix: string }> = {
  max_sesiones_por_usuario: { icon: MessageSquare, label: 'Máximo de sesiones por usuario al día', suffix: 'por día' },
  max_consultas_por_sesion: { icon: Hash, label: 'Máximo de consultas al día', suffix: 'por día' },
  max_tokens_por_sesion: { icon: Zap, label: 'Máximo de tokens al día', suffix: 'por día' },
};

const API_BASE = APP_CONFIG.BACKEND_URL;

export default function AdminConfigPage() {
  const { user } = useAuth();
  const { addNotification } = useNotifications();

  const [configs, setConfigs] = useState<ConfigItem[]>([]);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/v1/admin/config`);
      if (res.ok) {
        const data: ConfigItem[] = await res.json();
        setConfigs(data);
        const vals: Record<string, string> = {};
        data.forEach(c => { vals[c.clave] = c.valor; });
        setFormValues(vals);
      }
    } catch {
      addNotification({ type: 'error', title: 'Error', message: 'No se pudo cargar la configuración' });
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  async function handleSave() {
    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/api/v1/admin/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          configs: formValues,
          usuario_id: user?.id || '',
          usuario_nombre: user?.name || '',
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error al guardar');
      }

      const data = await res.json();
      setConfigs(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      addNotification({ type: 'success', title: 'Guardado', message: 'Configuración actualizada correctamente', autoClose: true });
    } catch (e: any) {
      addNotification({ type: 'error', title: 'Error', message: e.message });
    } finally {
      setSaving(false);
    }
  }

  const hasChanges = configs.some(c => formValues[c.clave] !== c.valor);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configuración de Plataforma</h1>
        <p className="text-sm text-gray-500 mt-1">Parámetros generales que aplican a todos los usuarios del sistema.</p>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-xl">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-800">Límites de uso diario</p>
          <p className="text-xs text-blue-600 mt-0.5">
            Estos parámetros controlan los límites de uso diario de la plataforma para cada usuario.
            <strong> Los contadores se reinician automáticamente cada día a las 00:00.</strong> Los cambios aplican de forma global para todos los usuarios.
          </p>
        </div>
      </div>

      {/* Config cards */}
      <div className="space-y-4">
        {configs.map(config => {
          const ui = CONFIG_UI[config.clave] || { icon: Wrench, label: config.clave, suffix: '' };
          const IconComp = ui.icon;

          return (
            <div
              key={config.id}
              className="bg-white rounded-xl border border-gray-100 p-6"
              style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
            >
              <div className="flex items-start justify-between gap-6">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#EEF2FF' }}>
                    <IconComp className="w-5 h-5" style={{ color: '#4F46E5' }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900">{ui.label}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{config.descripcion}</p>
                    {config.updated_by && (
                      <p className="text-[10px] text-gray-400 mt-1">
                        Última modificación por {config.updated_by}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    value={formValues[config.clave] || ''}
                    onChange={e => setFormValues(prev => ({ ...prev, [config.clave]: e.target.value }))}
                    className="w-32 px-3 py-2 text-sm text-right font-mono border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': '#EDAA00' } as React.CSSProperties}
                  />
                  <span className="text-xs text-gray-400 w-16">{ui.suffix}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40"
          style={{ backgroundColor: saved ? '#10B981' : '#1B2A4A' }}
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : saved ? (
            <Check className="w-4 h-4" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saved ? 'Guardado' : 'Guardar cambios'}
        </button>
      </div>
    </div>
  );
}
